import { Link } from 'react-router-dom';

const Section = ({ id, title, children }: { id?: string; title: string; children: React.ReactNode }) => (
  <section id={id} className="mb-8 scroll-mt-20">
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="text-sm text-gray-800 leading-relaxed space-y-2">{children}</div>
  </section>
);

const AIDisclosurePage = () => {
  const today = '13/04/2026';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <Link to="/quick-order" className="text-xs text-gray-700 hover:text-gray-900">&larr; Volver al asistente</Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">Información sobre el uso de Inteligencia Artificial</h1>
        <p className="text-sm text-gray-700 mt-2">Documento actualizado el {today}</p>
        <p className="text-xs text-gray-600 mt-1">
          Esta página da cumplimiento al artículo 50 del Reglamento (UE) 2024/1689 sobre Inteligencia Artificial
          y a los artículos 13 y 14 del Reglamento (UE) 2016/679 (RGPD).
        </p>
      </div>

      {/* Index */}
      <nav className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-8 text-xs">
        <p className="font-medium text-gray-900 mb-2">Índice</p>
        <ol className="list-decimal pl-4 space-y-0.5 text-gray-700">
          <li><a href="#s1" className="hover:text-gray-900 hover:underline">Identificación del responsable</a></li>
          <li><a href="#s2" className="hover:text-gray-900 hover:underline">Qué es el asistente y clasificación legal</a></li>
          <li><a href="#s3" className="hover:text-gray-900 hover:underline">Aviso de interacción con IA (art. 50 AI Act)</a></li>
          <li><a href="#s4" className="hover:text-gray-900 hover:underline">Limitaciones del sistema</a></li>
          <li><a href="#s5" className="hover:text-gray-900 hover:underline">Datos personales tratados</a></li>
          <li><a href="#s6" className="hover:text-gray-900 hover:underline">Finalidades del tratamiento</a></li>
          <li><a href="#s7" className="hover:text-gray-900 hover:underline">Base jurídica</a></li>
          <li><a href="#s8" className="hover:text-gray-900 hover:underline">Encargados y destinatarios</a></li>
          <li><a href="#s9" className="hover:text-gray-900 hover:underline">Transferencias internacionales</a></li>
          <li><a href="#s10" className="hover:text-gray-900 hover:underline">Plazo de conservación</a></li>
          <li><a href="#s11" className="hover:text-gray-900 hover:underline">Decisiones automatizadas y supervisión humana</a></li>
          <li><a href="#s12" className="hover:text-gray-900 hover:underline">Derechos del interesado</a></li>
          <li><a href="#s13" className="hover:text-gray-900 hover:underline">Medidas técnicas y organizativas</a></li>
          <li><a href="#s14" className="hover:text-gray-900 hover:underline">Cumplimiento normativo</a></li>
          <li><a href="#s15" className="hover:text-gray-900 hover:underline">Contacto y reclamaciones</a></li>
        </ol>
      </nav>

      <Section id="s1" title="1. Identificación del responsable del tratamiento">
        <p>
          <strong>Responsable:</strong> ValleGalbarro, S.L. (en adelante, &laquo;ValleGalbarro&raquo;).<br />
          <strong>Contacto en materia de protección de datos:</strong>{' '}
          <a href="mailto:protecciondatos@vallegalbarro.es" className="text-blue-600 hover:underline">
            protecciondatos@vallegalbarro.es
          </a>
        </p>
        <p>
          A los efectos del Reglamento (UE) 2024/1689 (en adelante, &laquo;AI Act&raquo;), ValleGalbarro actúa como
          <strong> responsable del despliegue</strong> (deployer) del sistema de IA descrito en este documento.
        </p>
      </Section>

      <Section id="s2" title="2. Qué es el asistente de Pedido Rápido y su clasificación">
        <p>
          El &laquo;Asistente de Pedido Rápido&raquo; es una funcionalidad opcional de la plataforma de
          ValleGalbarro que permite a usuarios autenticados crear pedidos mediante una conversación en lenguaje
          natural con un sistema de Inteligencia Artificial basado en un modelo de lenguaje de gran tamaño (LLM).
        </p>
        <p>
          De conformidad con el AI Act, este sistema se clasifica como un <strong>sistema de IA destinado a
          interactuar directamente con personas físicas</strong> (artículo 50.1 AI Act) y, en lo que respecta
          al modelo subyacente, como un <strong>modelo de IA de propósito general</strong> proporcionado por
          un tercero. No se trata de un sistema de alto riesgo conforme al Anexo III del AI Act, ya que su
          finalidad es exclusivamente comercial (creación de pedidos B2B) y no afecta a derechos fundamentales,
          empleo, educación, servicios esenciales ni administración de justicia.
        </p>
      </Section>

      <Section id="s3" title="3. Aviso de interacción con un sistema de IA (artículo 50.1 del AI Act)">
        <p>
          Conforme al artículo 50.1 del AI Act, te informamos de manera clara, distinguible y a más tardar
          en el momento de la primera interacción de que <strong>estás interactuando con un sistema de
          Inteligencia Artificial y no con una persona humana</strong>.
        </p>
        <p>
          Para consultas que requieran asesoramiento humano (incidencias, reclamaciones, productos no
          listados, condiciones especiales, etc.) puedes contactar con tu comercial asignado o con nuestro
          servicio de atención al cliente a través de los canales habituales.
        </p>
      </Section>

      <Section id="s4" title="4. Limitaciones del sistema y posibles errores">
        <p>
          Los modelos de lenguaje de gran tamaño pueden generar contenido inexacto, inventado (&laquo;alucinaciones&raquo;)
          o malinterpretar instrucciones. Específicamente, el asistente puede:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Confundir productos similares o seleccionar variantes incorrectas (tamaños, formatos).</li>
          <li>Calcular cantidades erróneas o no respetar los pasos mínimos de packaging.</li>
          <li>No detectar referencias o EAN existentes en el catálogo.</li>
          <li>Generar respuestas con información obsoleta sobre productos.</li>
        </ul>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
          <p className="text-amber-900">
            <strong>Importante:</strong> antes de finalizar cualquier pedido, revisa siempre el contenido
            del carrito en la página de checkout. La confirmación final del pedido la realizas tú como
            usuario; el asistente nunca completa una compra de forma automática.
          </p>
        </div>
      </Section>

      <Section id="s5" title="5. Datos personales tratados">
        <p>Cuando utilizas el asistente, se tratan las siguientes categorías de datos:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Datos de identificación:</strong> nombre y apellidos, dirección de correo electrónico, identificador de usuario interno.</li>
          <li><strong>Datos comerciales:</strong> rol en la plataforma (cliente, comercial, distribuidor, etc.), tarifa asignada, porcentaje de descuento personal y de pronto pago.</li>
          <li><strong>Contenido de las conversaciones:</strong> los mensajes que escribes en el chat y las respuestas generadas por el sistema.</li>
          <li><strong>Datos transaccionales (bajo demanda):</strong> historial de pedidos previos, únicamente cuando solicitas expresamente al asistente que los consulte (p. ej., &laquo;repíteme el último pedido&raquo;).</li>
          <li><strong>Estado del carrito:</strong> productos, cantidades y precios actualmente en tu carrito.</li>
          <li><strong>Metadatos técnicos:</strong> hora de la solicitud, identificador de sesión y token de autenticación (no se envían al proveedor de IA).</li>
        </ul>
        <p className="font-medium mt-3">Categorías de datos que NO se tratan ni se envían a la IA:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Contraseñas o credenciales de acceso.</li>
          <li>Datos bancarios, IBAN o información de tarjetas de pago.</li>
          <li>Datos de salud, religión, opiniones políticas u otras categorías especiales del artículo 9 RGPD.</li>
          <li>Datos personales de terceros que no sean clientes vinculados a tu cuenta.</li>
        </ul>
      </Section>

      <Section id="s6" title="6. Finalidades del tratamiento">
        <p>Los datos descritos se tratan exclusivamente con las siguientes finalidades:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Permitirte crear, modificar y consultar pedidos mediante lenguaje natural.</li>
          <li>Calcular precios, descuentos y disponibilidad de stock conforme a tu tarifa contratada.</li>
          <li>Mantener un historial de tus conversaciones para que puedas retomarlas posteriormente.</li>
          <li>Detectar y prevenir usos abusivos del sistema (medida de seguridad).</li>
          <li>Mejorar la calidad del servicio mediante el análisis agregado y anonimizado de errores.</li>
        </ul>
        <p>
          <strong>No se utilizan tus datos para entrenar modelos de IA</strong> propios ni de terceros, ni
          para perfilado comercial fuera del contexto del asistente.
        </p>
      </Section>

      <Section id="s7" title="7. Base jurídica del tratamiento">
        <p>El tratamiento se ampara en las siguientes bases jurídicas del artículo 6 del RGPD:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Ejecución de un contrato</strong> (art. 6.1.b RGPD): los datos necesarios para crear el
            pedido y aplicar las condiciones comerciales pactadas se tratan en el marco de la relación
            contractual entre tu empresa y ValleGalbarro.
          </li>
          <li>
            <strong>Consentimiento expreso</strong> (art. 6.1.a RGPD): el uso del asistente conversacional con
            IA es opcional. Antes de la primera interacción se solicita tu consentimiento informado mediante
            un aviso visible. Puedes seguir usando el catálogo y los canales tradicionales sin necesidad de
            otorgarlo.
          </li>
          <li>
            <strong>Interés legítimo</strong> (art. 6.1.f RGPD): para garantizar la seguridad del sistema,
            prevenir fraudes y mantener la integridad operativa del servicio. Hemos realizado el preceptivo
            juicio de ponderación.
          </li>
        </ul>
      </Section>

      <Section id="s8" title="8. Encargados del tratamiento y destinatarios">
        <p>
          Para prestar el servicio recurrimos a los siguientes encargados del tratamiento, que actúan bajo
          contratos conformes al artículo 28 del RGPD:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Fireworks AI, Inc.</strong> (Estados Unidos): proveedor del modelo de lenguaje (LLM) que
            procesa el contenido de los mensajes y genera las respuestas. Los datos enviados se limitan al
            mensaje del usuario, el contexto del sistema (nombre, rol, tarifa, descuento) y los resultados de
            las herramientas internas (búsquedas y operaciones de carrito).
          </li>
          <li>
            <strong>Proveedor de servicios cloud con zona de disponibilidad en la Unión Europea</strong>:
            aloja nuestro backend, base de datos y caché. Aquí se almacenan las conversaciones, el carrito y
            el historial de pedidos.
          </li>
          <li>
            <strong>Cloudflare, Inc.</strong>: red de entrega de contenido (CDN) y protección frente a ataques.
            No tiene acceso al contenido cifrado.
          </li>
        </ul>
        <p>
          Los datos no se ceden a terceros distintos de los anteriores salvo obligación legal.
        </p>
      </Section>

      <Section id="s9" title="9. Transferencias internacionales de datos">
        <p>
          La utilización de Fireworks AI implica una <strong>transferencia internacional de datos a Estados Unidos</strong>.
          Esta transferencia se realiza con las siguientes garantías de conformidad con el Capítulo V del RGPD:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Cláusulas Contractuales Tipo (CCT/SCC)</strong> aprobadas por la Comisión Europea
            mediante Decisión de Ejecución (UE) 2021/914, firmadas con el proveedor.
          </li>
          <li>
            <strong>EU-US Data Privacy Framework</strong> (Decisión de adecuación de la Comisión de 10 de
            julio de 2023), en la medida en que el proveedor esté certificado.
          </li>
          <li>
            Medidas suplementarias: pseudonimización del identificador interno de usuario, exclusión de
            datos sensibles del payload, cifrado en tránsito (TLS 1.3).
          </li>
        </ul>
        <p>
          Puedes solicitar copia de las garantías aplicables escribiendo a{' '}
          <a href="mailto:protecciondatos@vallegalbarro.es" className="text-blue-600 hover:underline">
            protecciondatos@vallegalbarro.es
          </a>.
        </p>
      </Section>

      <Section id="s10" title="10. Plazo de conservación">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Conversaciones del asistente:</strong> mientras tu cuenta esté activa o hasta que las
            elimines manualmente desde la barra lateral del propio asistente. La eliminación es definitiva e
            inmediata en nuestros sistemas.
          </li>
          <li>
            <strong>Datos enviados al proveedor de IA:</strong> Fireworks AI no conserva los mensajes para
            entrenamiento. Los datos se procesan transitoriamente para generar la respuesta y se descartan.
          </li>
          <li>
            <strong>Pedidos generados:</strong> conforme a la normativa fiscal y mercantil vigente, mínimo
            6 años (art. 30 del Código de Comercio) y hasta 10 años para obligaciones tributarias.
          </li>
          <li>
            <strong>Datos de cuenta:</strong> hasta la cancelación de la cuenta y posteriormente el plazo
            legal de prescripción de acciones derivadas del contrato.
          </li>
        </ul>
      </Section>

      <Section id="s11" title="11. Decisiones automatizadas y supervisión humana">
        <p>
          El asistente <strong>no adopta decisiones individuales automatizadas con efectos jurídicos
          significativos</strong> sobre los usuarios en el sentido del artículo 22 del RGPD. La confirmación
          final del pedido, el pago y la aceptación de las condiciones se realiza siempre mediante una acción
          explícita del usuario en la página de checkout.
        </p>
        <p>
          Aplicamos las medidas de supervisión humana previstas en el artículo 14 del AI Act:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>El usuario puede en todo momento ignorar las sugerencias del asistente, modificar el carrito manualmente o usar los canales tradicionales.</li>
          <li>El equipo comercial revisa los pedidos generados para detectar anomalías sistemáticas.</li>
          <li>El sistema solo añade productos al carrito tras una instrucción explícita del usuario en lenguaje natural.</li>
        </ul>
      </Section>

      <Section id="s12" title="12. Derechos del interesado">
        <p>
          Como titular de los datos, puedes ejercer en cualquier momento los siguientes derechos previstos
          en los artículos 15 a 22 del RGPD:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Acceso</strong>: obtener confirmación y copia de los datos que tratamos sobre ti.</li>
          <li><strong>Rectificación</strong>: corregir datos inexactos o incompletos.</li>
          <li><strong>Supresión</strong> (&laquo;derecho al olvido&raquo;): eliminar tus datos cuando ya no sean necesarios o retires el consentimiento.</li>
          <li><strong>Limitación</strong> del tratamiento en los supuestos del artículo 18 RGPD.</li>
          <li><strong>Portabilidad</strong>: recibir tus datos en un formato estructurado y de uso común.</li>
          <li><strong>Oposición</strong>: oponerte al tratamiento basado en interés legítimo.</li>
          <li><strong>Retirada del consentimiento</strong> en cualquier momento, sin que ello afecte a la licitud del tratamiento previo.</li>
          <li><strong>No ser objeto de decisiones automatizadas</strong> (art. 22 RGPD).</li>
        </ul>
        <p className="mt-3">
          Para ejercer cualquiera de estos derechos, escribe a{' '}
          <a href="mailto:protecciondatos@vallegalbarro.es" className="text-blue-600 hover:underline">
            protecciondatos@vallegalbarro.es
          </a>{' '}
          adjuntando copia de tu DNI/NIE o documento equivalente. Responderemos en el plazo máximo de un mes (art. 12.3 RGPD).
        </p>
      </Section>

      <Section id="s13" title="13. Medidas técnicas y organizativas de seguridad">
        <p>
          Aplicamos las medidas exigidas por el artículo 32 del RGPD, entre ellas:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Cifrado en tránsito mediante TLS 1.3 entre todos los componentes del sistema.</li>
          <li>Autenticación basada en tokens JWT con expiración y rotación de claves.</li>
          <li>Control de acceso por roles (RBAC) y principio de mínimo privilegio.</li>
          <li>Filtrado de contenido en el system prompt para mitigar inyecciones de prompt y exfiltración de datos.</li>
          <li>Registros de auditoría de las llamadas al modelo de IA y a las herramientas internas.</li>
          <li>Revisión periódica de los acuerdos con encargados del tratamiento.</li>
          <li>Plan de respuesta a brechas de seguridad y notificación a la AEPD en menos de 72 horas si procede.</li>
        </ul>
      </Section>

      <Section id="s14" title="14. Cumplimiento normativo">
        <p>El sistema y este documento dan cumplimiento a las siguientes normas:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Reglamento (UE) 2024/1689</strong>, de 13 de junio de 2024, por el que se establecen normas armonizadas en materia de inteligencia artificial (Ley de Inteligencia Artificial). En particular, su artículo 50 sobre obligaciones de transparencia.</li>
          <li><strong>Reglamento (UE) 2016/679</strong>, de 27 de abril de 2016, relativo a la protección de las personas físicas en lo que respecta al tratamiento de datos personales (RGPD). Artículos 5, 6, 12, 13, 14, 15-22, 28, 32, 35 y 44-49.</li>
          <li><strong>Ley Orgánica 3/2018</strong>, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD).</li>
          <li><strong>Ley 34/2002</strong>, de 11 de julio, de servicios de la sociedad de la información y de comercio electrónico (LSSI-CE).</li>
          <li>Guía de la <strong>Agencia Española de Protección de Datos (AEPD)</strong> &laquo;Adecuación al RGPD de tratamientos que incorporan Inteligencia Artificial&raquo;.</li>
        </ul>
      </Section>

      <Section id="s15" title="15. Contacto y reclamaciones">
        <p>
          Para cualquier consulta relacionada con el tratamiento de tus datos personales o el uso de este
          sistema de IA, puedes escribirnos a:
        </p>
        <p className="bg-gray-50 border border-gray-200 rounded-lg p-3 my-2">
          <strong>Correo electrónico:</strong>{' '}
          <a href="mailto:protecciondatos@vallegalbarro.es" className="text-blue-600 hover:underline">
            protecciondatos@vallegalbarro.es
          </a>
        </p>
        <p>
          Si consideras que el tratamiento de tus datos no se ajusta a la normativa, tienes derecho a
          presentar una reclamación ante la <strong>Agencia Española de Protección de Datos</strong>:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sede electrónica: <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.aepd.es</a></li>
          <li>Dirección postal: C/ Jorge Juan, 6. 28001 Madrid.</li>
          <li>Teléfono: 901 100 099 / 91 266 35 17.</li>
        </ul>
        <p className="mt-3 text-xs text-gray-700">
          También puedes acudir, con carácter previo, al delegado de protección de datos del responsable a
          través del correo electrónico indicado.
        </p>
      </Section>

      <div className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-600">
        <p>
          Documento elaborado conforme al Reglamento (UE) 2024/1689 (AI Act), el Reglamento (UE) 2016/679
          (RGPD) y la Ley Orgánica 3/2018 (LOPDGDD). Última revisión: {today}.
        </p>
      </div>
    </div>
  );
};

export default AIDisclosurePage;
